<script setup lang="ts">
  import { computed } from 'vue'
  import { HoverCard, HoverCardContent, HoverCardTrigger } from '../hover-card'
  import type {
    KeyboardProps,
    CanvasDimensions,
    KeyConfig,
    KeyboardConfig,
    KeyboardEmits,
    KeyboardKeyEventPayload,
  } from './types'
  import KeyboardKey from './KeyboardKey.vue'

  defineOptions({
    name: 'Keyboard',
  })

  const emits = defineEmits<KeyboardEmits>()

  const props = withDefaults(defineProps<KeyboardProps>(), {
    gap: 2,
  })

  const unit = computed(() => props.unit ?? props.keyboardConfig.unit ?? 40)
  const pitch = computed(() => unit.value + props.gap) // 网格步进 = 单位 + 间距

  /**
   * 扁平化键盘布局配置，并计算按键实际位置和尺寸：
   */
  const normalizeKeyConfigs = (keys: KeyboardConfig['keys']): KeyConfig[] => {
    if (!keys.length) return []

    const normalized: KeyConfig[] = []

    keys.forEach((rowKeys, rowIndex) => {
      let cursorX = 0

      rowKeys.forEach((rowKey) => {
        const { rect, ...rest } = rowKey
        cursorX += rect?.x || 0
        const cursorY = rowIndex + (rect?.y || 0)
        const keyWidthUnit = rect?.w ?? 1
        const keyHeightUnit = rect?.h ?? 1

        normalized.push({
          ...rest,
          rect: {
            x: cursorX,
            y: cursorY,
            w: keyWidthUnit,
            h: keyHeightUnit,
          },
          layout: {
            left: Math.round(cursorX * pitch.value),
            top: Math.round(cursorY * pitch.value),
            width: Math.round(keyWidthUnit * unit.value + (keyWidthUnit - 1) * props.gap),
            height: Math.round(keyHeightUnit * unit.value + (keyHeightUnit - 1) * props.gap),
          },
        } as KeyConfig)

        cursorX += rect?.w || 1
      })
    })

    return normalized
  }

  const keyConfigs = computed<KeyConfig[]>(() => normalizeKeyConfigs(props.keyboardConfig.keys))

  /**
   * 键盘画布大小
   */
  const svgCanvasSize = computed<CanvasDimensions>(() => {
    let maxRight = 0
    let maxBottom = 0

    keyConfigs.value.forEach((key: KeyConfig) => {
      const w = key.rect?.w ?? 1
      const h = key.rect?.h ?? 1

      const left = (key.rect?.x ?? 0) * pitch.value
      const top = (key.rect?.y ?? 0) * pitch.value
      const width = w * unit.value + (w - 1) * props.gap
      const height = h * unit.value + (h - 1) * props.gap

      maxRight = Math.max(maxRight, left + width)
      maxBottom = Math.max(maxBottom, top + height)
    })

    return {
      width: Math.round(maxRight),
      height: Math.round(maxBottom),
    }
  })

  const handleKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    emits('key-event', ctx)
  }

  /**
   * 按键是否启用 tooltip 功能
   * @param keyLayout
   */
  const isKeyTooltipEnabled = (keyLayout: KeyConfig) => {
    const enabled = props.keyTooltip?.enabled
    if (enabled == null) return false
    if (typeof enabled === 'boolean') return enabled
    return enabled(keyLayout)
  }

  /**
   * 获取按键 tooltip open 状态
   * @param keyLayout
   */
  const getKeyTooltipOpen = (keyLayout: KeyConfig) => {
    return props.keyTooltip?.open?.(keyLayout)
  }

  /**
   * 处理按键样式
   * @param keyLayout
   */
  const getKeyClass = (keyLayout: KeyConfig) => {
    if (typeof props.keyClass !== 'function') return props.keyClass
    return props.keyClass(keyLayout)
  }

  /**
   * 处理按键禁用状态
   * @param keyLayout
   */
  const getKeyDisabled = (keyLayout: KeyConfig) => {
    if (typeof props.keyDisabled !== 'function') return props.keyDisabled
    return props.keyDisabled(keyLayout)
  }

  /**
   * 处理按键行内样式
   * @param keyLayout
   */
  const getKeyStyle = (keyLayout: KeyConfig) => {
    if (typeof props.keyStyle !== 'function') return props.keyStyle
    return props.keyStyle(keyLayout)
  }

  const handleKeyTooltipOpenChange = (open: boolean, keyLayout: KeyConfig) => {
    props.keyTooltip?.onOpenChange?.(open, keyLayout)
  }
</script>

<template>
  <div
    class="relative"
    :style="{ width: `${svgCanvasSize.width}px`, height: `${svgCanvasSize.height}px` }"
  >
    <slot name="widget" />
    <template v-for="config in keyConfigs" :key="config.keyCode">
      <HoverCard
        v-if="isKeyTooltipEnabled(config)"
        :open="getKeyTooltipOpen(config)"
        :close-delay="props.keyTooltip?.closeDelay"
        :open-delay="200"
        @update:open="(open: boolean) => handleKeyTooltipOpenChange(open, config)"
      >
        <HoverCardTrigger as-child>
          <KeyboardKey
            :config="config"
            :unit="unit"
            :native-event-names="props.nativeEventNames"
            :class="getKeyClass(config)"
            :disabled="getKeyDisabled(config)"
            :style="getKeyStyle(config)"
            @key-event="handleKeyEvent"
          >
            <template #label="slotProps">
              <slot name="key-label" :config="slotProps.config" />
            </template>
          </KeyboardKey>
        </HoverCardTrigger>
        <HoverCardContent
          :side="props.keyTooltip?.side"
          :side-offset="props.keyTooltip?.sideOffset"
          :class="props.keyTooltip?.class"
        >
          <slot name="key-tooltip" :config="config" />
        </HoverCardContent>
      </HoverCard>

      <KeyboardKey
        v-else
        :native-event-names="props.nativeEventNames"
        :config="config"
        :unit="unit"
        :class="getKeyClass(config)"
        :style="getKeyStyle(config)"
        :disabled="getKeyDisabled(config)"
        @key-event="handleKeyEvent"
      >
        <template #label="slotProps">
          <slot name="key-label" :config="slotProps.config" />
        </template>
      </KeyboardKey>
    </template>
  </div>
</template>

<style scoped></style>
